import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    flexRender,
} from '@tanstack/react-table';
import EditModal from './EditModal'; // Імпортуємо модальне вікно
import ProductModal from './ProductModal'; // Імпортуємо нову модалку
import '../../css/AdminTable.css'; // Використовуємо ті самі стилі
import '../../css/AdminModal.css'; // Додаємо стилі модалки, якщо ще не імпортовані
import { BACKEND_URL } from '../../config';

// Ключ для localStorage для збереженої категорії
const LOCAL_STORAGE_KEY_CATEGORY = 'adminSelectedCategoryId';

// Компонент для редагованої комірки (з можливістю вказати тип)
const EditableCell = ({ getValue, row, column, table, type = 'text' }) => {
    const initialValue = getValue();
    const [value, setValue] = useState(initialValue);

    const onBlur = () => {
        // Конвертуємо у числовий тип, якщо потрібно
        const processedValue = type === 'number' ? (value === '' ? null : Number(value)) : value;
        table.options.meta?.updateData(row.index, column.id, processedValue);
    };

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    // Для типу number використовуємо type="number", для інших - type="text"
    return (
        <input
            type={type === 'number' ? 'number' : 'text'}
            value={value === null || value === undefined ? '' : value} // Показуємо порожній рядок для null/undefined
            onChange={e => setValue(e.target.value)}
            onBlur={onBlur}
            className="editable-cell-input"
            // Додамо step для числових полів, якщо потрібно
            {...(type === 'number' && { step: "0.01" })} 
        />
    );
};

// Основний компонент
// base64-encoded confirmation phrase (not stored as plain text)
const CONFIRM_HASH = 'ZGVsZXRlYWxs';

const ProductsAdmin = () => {
    const [data, setData] = useState([]); // Перейменовуємо state для ясності
    const [originalDataMap, setOriginalDataMap] = useState({}); // Використовуємо Map для швидкого пошуку
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Стан для модального вікна "Видалити всі"
    const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
    const [deleteAllPassword, setDeleteAllPassword] = useState('');
    const [deleteAllLoading, setDeleteAllLoading] = useState(false);
    const [deleteAllError, setDeleteAllError] = useState('');
    const [pageCount, setPageCount] = useState(0); // Загальна кількість сторінок
    const [savingProductId, setSavingProductId] = useState(null); // ID товару що зберігається
    const [isModalSaving, setIsModalSaving] = useState(false); // Індикатор збереження через модальне вікно
    
    // Refs для синхронізації скролів
    const topScrollRef = useRef(null);
    const bottomScrollRef = useRef(null);
    
    // Ref для збереження позиції скролу
    const scrollPositionRef = useRef(0);
    
    // Флаг для контролю перезавантаження з збереженням скролу
    const shouldPreserveScrollRef = useRef(false);
    
    // useEffect для відновлення позиції скролу після оновлення даних
    useEffect(() => {
        if (shouldPreserveScrollRef.current && scrollPositionRef.current > 0) {
            // Подвійний requestAnimationFrame для гарантованого відновлення після повного рендеру
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    window.scrollTo({
                        top: scrollPositionRef.current,
                        behavior: 'instant' // Миттєво, без анімації
                    });
                    console.log('[ProductsAdmin] Scroll position restored to:', scrollPositionRef.current);
                    scrollPositionRef.current = 0;
                    shouldPreserveScrollRef.current = false;
                });
            });
        }
    }, [data]); // Спрацьовує коли змінюються дані

    // Стан пагінації для react-table
    const [{ pageIndex, pageSize }, setPagination] = useState({
        pageIndex: 0,
        pageSize: 50,
    });

    // Стан для списку категорій та вибраної категорії
    const [allCategories, setAllCategories] = useState([]);
    // Ініціалізуємо вибрану категорію з localStorage
    const [selectedCategoryId, setSelectedCategoryId] = useState(() => {
        return localStorage.getItem(LOCAL_STORAGE_KEY_CATEGORY) || ''; 
    });

    // Стан для марок та моделей
    const [brands, setBrands] = useState([]);
    const [models, setModels] = useState([]);

    // Стан для модального вікна РЕДАГУВАННЯ ОКРЕМИХ ПОЛІВ
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editModalData, setEditModalData] = useState({
        value: null,
        rowIndex: null,
        columnId: null,
        fieldType: 'text',
        fieldLabel: ''
    });

    // Стан для модального вікна СТВОРЕННЯ/РЕДАГУВАННЯ ТОВАРУ
    const [isProductModalOpen, setIsProductModalOpen] = useState(false);
    // Стан для початкових даних модального вікна (для створення нового товару)
    const [productModalInitialData, setProductModalInitialData] = useState({});
    // Стан для завантаження файлу
    const [uploadingFile, setUploadingFile] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [uploadSuccess, setUploadSuccess] = useState(false);

    // Стан для завантаження компресорів
    const [uploadingCompressor, setUploadingCompressor] = useState(false);
    const [uploadCompressorError, setUploadCompressorError] = useState(null);
    const [uploadCompressorSuccess, setUploadCompressorSuccess] = useState(false);

    // Стан для завантаження ЕБУ
    const [uploadingEcu, setUploadingEcu] = useState(false);
    const [uploadEcuError, setUploadEcuError] = useState(null);
    const [uploadEcuSuccess, setUploadEcuSuccess] = useState(false);
    // Можливо, стан для даних редагованого товару (для майбутнього)
    // const [editingProductData, setEditingProductData] = useState(null); 

    // Синхронізація скролів та динамічна ширина
    useEffect(() => {
        const topScroll = topScrollRef.current;
        const bottomScroll = bottomScrollRef.current;
        
        if (!topScroll || !bottomScroll) return;
        
        const table = bottomScroll.querySelector('.admin-table');
        const scrollContent = topScroll.querySelector('.admin-table-scroll-content');
        
        if (!table || !scrollContent) return;
        
        // Функція для оновлення ширини
        const updateWidth = () => {
            const tableWidth = table.scrollWidth;
            scrollContent.style.width = `${tableWidth}px`;
        };
        
        // Початкове оновлення ширини з невеликою затримкою
        setTimeout(updateWidth, 0);
        
        // Відстежуємо зміни розміру таблиці
        const resizeObserver = new ResizeObserver(updateWidth);
        resizeObserver.observe(table);
        
        // Синхронізація скролів
        const handleTopScroll = () => {
            bottomScroll.scrollLeft = topScroll.scrollLeft;
        };
        
        const handleBottomScroll = () => {
            topScroll.scrollLeft = bottomScroll.scrollLeft;
        };
        
        topScroll.addEventListener('scroll', handleTopScroll);
        bottomScroll.addEventListener('scroll', handleBottomScroll);
        
        return () => {
            topScroll.removeEventListener('scroll', handleTopScroll);
            bottomScroll.removeEventListener('scroll', handleBottomScroll);
            resizeObserver.disconnect();
        };
    }, [data]);

    // Завантаження списку категорій для фільтра
    useEffect(() => {
        fetch(`${BACKEND_URL}/categories`)
            .then(res => res.ok ? res.json() : Promise.reject(new Error('Failed to load categories')))
            .then(setAllCategories)
            .catch(err => {
                console.error("Помилка завантаження категорій для фільтра:", err);
                setError('Не вдалося завантажити список категорій.'); // Повідомляємо про помилку
            });
    }, []);

    // Завантаження марок та моделей
    useEffect(() => {
        // Завантажуємо марки
        fetch(`${BACKEND_URL}/cars/brands`)
            .then(res => res.ok ? res.json() : Promise.reject(new Error('Failed to load brands')))
            .then(setBrands)
            .catch(err => {
                console.error("Помилка завантаження марок:", err);
            });

        // Завантажуємо моделі
        fetch(`${BACKEND_URL}/cars/models`)
            .then(res => res.ok ? res.json() : Promise.reject(new Error('Failed to load models')))
            .then(setModels)
            .catch(err => {
                console.error("Помилка завантаження моделей:", err);
            });
    }, []);

    // Функція для завантаження даних товарів з урахуванням категорії та пагінації
    const fetchData = useCallback(async (page = 0, size = 50, categoryId, preserveScroll = false) => {
        if (!categoryId || categoryId === '') {
            setData([]);
            setPageCount(0);
            setOriginalDataMap({});
            setLoading(false);
            return;
        }
        
        // Зберігаємо позицію скролу якщо потрібно
        if (preserveScroll) {
            scrollPositionRef.current = window.scrollY;
        }
        
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                page: page,
                pageSize: size,
                categoryId: categoryId,
            });
            const response = await fetch(`${BACKEND_URL}/products/by-filters?${params.toString()}`, {
                headers: { /* 'Authorization': ... */ }
            });
            if (!response.ok) {
                throw new Error(`HTTP помилка! Статус: ${response.status}`);
            }
            const responseData = await response.json();
            setData(responseData.products || []);
            setPageCount(responseData.totalPages || 0);
            const newOriginals = {};
            (responseData.products || []).forEach(item => {
                newOriginals[item.id] = JSON.parse(JSON.stringify(item));
            });
            setOriginalDataMap(prev => ({ ...prev, ...newOriginals }));
        } catch (err) {
            console.error('Помилка завантаження товарів:', err);
            setError(err.message || 'Не вдалося завантажити товари.');
            setData([]);
            setPageCount(0);
        } finally {
            setLoading(false);
            
            // Відновлюємо позицію скролу якщо потрібно
            if (preserveScroll && scrollPositionRef.current > 0) {
                // Використовуємо setTimeout щоб дати час браузеру відрендерити таблицю
                setTimeout(() => {
                    window.scrollTo(0, scrollPositionRef.current);
                    scrollPositionRef.current = 0; // Скидаємо
                }, 100);
            }
        }
    }, [setData, setPageCount, setOriginalDataMap, setLoading, setError]);

    // Завантажуємо дані ТІЛЬКИ ЯКЩО вибрана категорія
    useEffect(() => {
        if (selectedCategoryId && selectedCategoryId !== '') {
            // Використовуємо флаг для збереження скролу
            const preserveScroll = shouldPreserveScrollRef.current;
            shouldPreserveScrollRef.current = false; // Скидаємо флаг
            fetchData(pageIndex, pageSize, selectedCategoryId, preserveScroll);
        } else {
             // Якщо категорія скинута на "Всі", очищуємо дані
            setData([]);
            setPageCount(0);
            setOriginalDataMap({});
        }
        // Зберігаємо вибрану категорію в localStorage ТУТ
        // Зберігаємо навіть порожній рядок, якщо категорія не вибрана
        localStorage.setItem(LOCAL_STORAGE_KEY_CATEGORY, selectedCategoryId);

    }, [fetchData, pageIndex, pageSize, selectedCategoryId]);

    // Обробник зміни категорії у випадаючому списку
    const handleCategoryChange = (event) => {
        const newCategoryId = event.target.value;
        setSelectedCategoryId(newCategoryId);
        setPageIndex(0); 
        // Зберігаємо нову категорію в localStorage одразу при зміні
        // localStorage.setItem(LOCAL_STORAGE_KEY_CATEGORY, newCategoryId); // Перенесено в useEffect
        // Очищуємо повідомлення про помилки та успіх завантаження
        setUploadError(null);
        setUploadSuccess(false);
    };
    
    // Функція для скидання сторінки при зміні фільтра
    const setPageIndex = (index) => {
        setPagination(prev => ({ ...prev, pageIndex: index }));
    };

    // Конфігурація пагінації для react-table
    const pagination = useMemo(() => ({ pageIndex, pageSize }), [pageIndex, pageSize]);

    // Функція для відкриття модального вікна РЕДАГУВАННЯ ОКРЕМОГО ПОЛЯ
    const openEditModal = (cell) => {
        const { row, column } = cell;
        
        // Спеціальна обробка для колонки brandModel
        if (column.id === 'brandModel') {
            setEditModalData({
                value: {
                    brandId: row.original.model?.brand?.id || '',
                    modelId: row.original.model?.id || ''
                },
                rowIndex: row.index,
                columnId: column.id,
                fieldType: 'editBrandModel', // Спеціальний тип
                fieldLabel: column.columnDef.header
            });
            setIsEditModalOpen(true);
            return;
        }
        
        // Розширюємо карту типів для інших полів
        const fieldTypeMap = {
            description: 'textarea',
            name_ua: 'textarea',
            photo: 'textarea',
            link: 'textarea',
            price: 'number',
            originalPrice: 'number',
            deliveryPrice: 'number',
            originalId: 'number',
            categoryId: 'number'
        };
        setEditModalData({
            value: cell.getValue(),
            rowIndex: row.index,
            columnId: column.id,
            fieldType: fieldTypeMap[column.id] || 'text',
            fieldLabel: column.columnDef.header
        });
        setIsEditModalOpen(true);
    };

    // Функція для збереження даних з модального вікна РЕДАГУВАННЯ ОКРЕМОГО ПОЛЯ
    const handleSingleFieldModalSave = (newValue) => {
        const { rowIndex, columnId } = editModalData;
        if (rowIndex !== null && columnId !== null) {
            // Зберігаємо позицію скролу перед оновленням стану
            scrollPositionRef.current = window.scrollY;
            shouldPreserveScrollRef.current = true;

            // Спеціальна обробка для brandModel
            if (columnId === 'brandModel') {
                setData(old =>
                    old.map((row, index) => {
                        if (index === rowIndex) {
                            const updatedRow = { ...row };
                            // Якщо modelId не вибрано, встановлюємо model в null
                            if (!newValue.modelId) {
                                updatedRow.model = null;
                            } else {
                                // Знаходимо повну інформацію про модель
                                const selectedModel = models.find(m => m.id === Number(newValue.modelId));
                                if (selectedModel) {
                                    // Підтримуємо обидві структури моделей
                                    const modelBrandId = selectedModel.brandId || selectedModel.brand?.id;
                                    updatedRow.model = {
                                        id: selectedModel.id,
                                        name: selectedModel.name,
                                        brand: brands.find(b => b.id === modelBrandId)
                                    };
                                }
                            }
                            return updatedRow;
                        }
                        return row;
                    })
                );
            } else {
                table.options.meta?.updateData(rowIndex, columnId, newValue);
            }
        }
        setIsEditModalOpen(false); // Закриваємо модалку редагування поля
    };

    // Функція для відкриття модального вікна СТВОРЕННЯ ТОВАРУ
    const openCreateProductModal = () => {
        console.log('[ProductsAdmin] Opening create product modal with selectedCategoryId:', selectedCategoryId);
        const initialData = {
            categoryId: selectedCategoryId || '',
            name_ua: '',
            code1: '',
            code2: '',
            code3: '',
            numerkatalogowy: '',
            price: '',
            originalPrice: '',
            deliveryPrice: '',
            originalId: '',
            link: '',
            photo: '',
            description: ''
        };
        console.log('[ProductsAdmin] Setting initial data:', initialData);
        setProductModalInitialData(initialData);
        setIsProductModalOpen(true);
    };

    // Функція для відкриття модального вікна редагування товару
    const openProductModal = (productData) => {
        console.log('[ProductsAdmin] Opening edit product modal with data:', productData);
        setProductModalInitialData(productData);
        setIsProductModalOpen(true);
    };

    // Функція для завантаження файлу
    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Перевіряємо тип файлу
        if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls') && !file.name.endsWith('.csv')) {
            setUploadError('Будь ласка, виберіть Excel файл (.xlsx, .xls) або CSV файл (.csv)');
            return;
        }

        // Перевіряємо, чи вибрана категорія
        if (!selectedCategoryId) {
            setUploadError('Спочатку виберіть категорію для завантаження товарів');
            return;
        }

        setUploadingFile(true);
        setUploadError(null);
        setUploadSuccess(false);

        try {
            const authToken = localStorage.getItem('authToken');
            if (!authToken) {
                throw new Error('Токен авторизації не знайдено. Будь ласка, увійдіть знову.');
            }
            
            // Перевіряємо валідність токена перед завантаженням
            try {
                const validateResponse = await fetch(`${BACKEND_URL}/api/auth/validate`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${authToken}`,
                    }
                });
                
                if (!validateResponse.ok) {
                    throw new Error('Токен невалідний. Будь ласка, увійдіть знову.');
                }
                console.log('[ProductsAdmin] Token validation successful');
            } catch (validateError) {
                console.error('[ProductsAdmin] Token validation failed:', validateError);
                throw new Error('Токен невалідний. Будь ласка, увійдіть знову.');
            }
            
            const formData = new FormData();
            formData.append('file', file);
            formData.append('categoryId', selectedCategoryId);

            console.log('[ProductsAdmin] Sending request to:', `${BACKEND_URL}/products/upload-table`);
            console.log('[ProductsAdmin] Request headers:', {
                'Authorization': authToken ? 'Bearer [TOKEN]' : 'No token',
                'Content-Type': 'multipart/form-data'
            });
            
            const response = await fetch(`${BACKEND_URL}/products/upload-table`, {
                method: 'POST',
                headers: {
                    ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
                },
                body: formData,
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP помилка! Статус: ${response.status}, Текст: ${errorText || 'N/A'}`);
            }

            // Сервер повертає текст, а не JSON
            const result = await response.text();
            console.log('Файл успішно завантажено:', result);
            setUploadSuccess(true);
            
            // Перезавантажуємо дані після успішного завантаження зі збереженням позиції
            console.log('[ProductsAdmin] Reloading data after successful upload');
            await fetchData(pageIndex, pageSize, selectedCategoryId, true);
            
            // Очищуємо файл input
            event.target.value = '';
            
            // Очищуємо повідомлення про успіх через 5 секунд
            setTimeout(() => {
                setUploadSuccess(false);
            }, 5000);

        } catch (err) {
            console.error('Помилка завантаження файлу:', err);
            setUploadError(`Помилка завантаження файлу: ${err.message}`);
        } finally {
            setUploadingFile(false);
        }
    };

    const handleCompressorUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls') && !file.name.endsWith('.csv')) {
            setUploadCompressorError('Будь ласка, виберіть Excel файл (.xlsx, .xls) або CSV файл (.csv)');
            return;
        }

        if (!selectedCategoryId) {
            setUploadCompressorError('Спочатку виберіть категорію для завантаження компресорів');
            return;
        }

        setUploadingCompressor(true);
        setUploadCompressorError(null);
        setUploadCompressorSuccess(false);

        try {
            const authToken = localStorage.getItem('authToken');
            if (!authToken) throw new Error('Токен авторизації не знайдено. Будь ласка, увійдіть знову.');

            const formData = new FormData();
            formData.append('file', file);
            formData.append('categoryId', selectedCategoryId);

            const response = await fetch(`${BACKEND_URL}/products/upload-compressors`, {
                method: 'POST',
                headers: {
                    ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
                },
                body: formData,
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP помилка! Статус: ${response.status}, Текст: ${errorText || 'N/A'}`);
            }

            const result = await response.text();
            console.log('Компресори успішно завантажено:', result);
            setUploadCompressorSuccess(true);

            await fetchData(pageIndex, pageSize, selectedCategoryId, true);

            event.target.value = '';

            setTimeout(() => setUploadCompressorSuccess(false), 5000);

        } catch (err) {
            console.error('Помилка завантаження компресорів:', err);
            setUploadCompressorError(`Помилка: ${err.message}`);
        } finally {
            setUploadingCompressor(false);
        }
    };

    const handleEcuUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls') && !file.name.endsWith('.csv')) {
            setUploadEcuError('Будь ласка, виберіть Excel файл (.xlsx, .xls) або CSV файл (.csv)');
            return;
        }

        if (!selectedCategoryId) {
            setUploadEcuError('Спочатку виберіть категорію для завантаження ЕБУ');
            return;
        }

        setUploadingEcu(true);
        setUploadEcuError(null);
        setUploadEcuSuccess(false);

        try {
            const authToken = localStorage.getItem('authToken');
            if (!authToken) throw new Error('Токен авторизації не знайдено. Будь ласка, увійдіть знову.');

            const formData = new FormData();
            formData.append('file', file);
            formData.append('categoryId', selectedCategoryId);

            const response = await fetch(`${BACKEND_URL}/products/upload-ecu`, {
                method: 'POST',
                headers: {
                    ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
                },
                body: formData,
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP помилка! Статус: ${response.status}, Текст: ${errorText || 'N/A'}`);
            }

            const result = await response.text();
            console.log('ЕБУ успішно завантажено:', result);
            setUploadEcuSuccess(true);

            await fetchData(pageIndex, pageSize, selectedCategoryId, true);

            event.target.value = '';

            setTimeout(() => setUploadEcuSuccess(false), 5000);

        } catch (err) {
            console.error('Помилка завантаження ЕБУ:', err);
            setUploadEcuError(`Помилка: ${err.message}`);
        } finally {
            setUploadingEcu(false);
        }
    };

    // Функція СТВОРЕННЯ нового товару (реалізація)
    // Універсальна функція для створення та редагування товару
    const handleSaveProduct = async (productData) => {
        // НЕ встановлюємо loading:true щоб не приховувати таблицю
        setIsModalSaving(true); // Показуємо індикатор в модальному вікні
        setError(null);
        
        const isEditMode = !!productData.id; // Перевіряємо чи є ID (редагування) чи ні (створення)
        
        try {
            // Перевіряємо наявність токену
            const authToken = localStorage.getItem('authToken');
            if (!authToken) {
                throw new Error('Токен авторизації не знайдено. Будь ласка, увійдіть знову.');
            }
            
            // Перевіряємо валідність токена
            try {
                const validateResponse = await fetch(`${BACKEND_URL}/api/auth/validate`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${authToken}`,
                    }
                });
                
                if (!validateResponse.ok) {
                    localStorage.removeItem('authToken');
                    throw new Error('Ваш токен прострочено. Будь ласка, увійдіть знову.');
                }
                console.log(`[ProductsAdmin] Token validation successful for ${isEditMode ? 'edit' : 'create'} product`);
            } catch (validateError) {
                console.error('[ProductsAdmin] Token validation failed:', validateError);
                localStorage.removeItem('authToken');
                throw new Error('Ваш токен прострочено. Будь ласка, увійдіть знову.');
            }
            
            // Перетворюємо categoryId та modelId на об'єкти для бекенду
            const dataToSend = {
                ...productData,
                category: { id: productData.categoryId }, // Вкладаємо ID в об'єкт category
            };
            // Видаляємо непотрібне поле categoryId
            delete dataToSend.categoryId;
            
            // Додаємо model якщо є modelId
            if (productData.modelId) {
                dataToSend.model = { id: productData.modelId };
            } else {
                // Якщо modelId немає, встановлюємо null
                dataToSend.model = null;
            }
            // Видаляємо непотрібне поле modelId
            delete dataToSend.modelId; 

            console.log(`Відправка даних для ${isEditMode ? 'редагування' : 'створення'} товару:`, dataToSend);

            const url = isEditMode ? `${BACKEND_URL}/products/${productData.id}` : `${BACKEND_URL}/products`;
            const method = isEditMode ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`,
                },
                body: JSON.stringify(dataToSend),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP помилка! Статус: ${response.status}, Текст: ${errorText || 'N/A'}`);
            }

            const savedProduct = await response.json();
            console.log(`Товар успішно ${isEditMode ? 'оновлено' : 'створено'}:`, savedProduct);

            // Зберігаємо позицію скролу перед оновленням стану
            scrollPositionRef.current = window.scrollY;
            shouldPreserveScrollRef.current = true;

            if (isEditMode) {
                // Для редагування - оновлюємо існуючий товар в таблиці
                setData(prevData => prevData.map(item => item.id === savedProduct.id ? savedProduct : item));
                setOriginalDataMap(prevMap => ({ ...prevMap, [savedProduct.id]: savedProduct }));
            } else {
                // Для створення - додаємо новий товар на початок списку (без перезавантаження)
                setData(prevData => [savedProduct, ...prevData]);
                setOriginalDataMap(prevMap => ({ ...prevMap, [savedProduct.id]: savedProduct }));
            }
            setIsProductModalOpen(false); // Закриваємо модалку

        } catch (err) {
            console.error(`Помилка ${isEditMode ? 'редагування' : 'створення'} товару:`, err);
            setError(`Помилка ${isEditMode ? 'редагування' : 'створення'} товару: ${err.message}`);
            
            // Якщо токен протермінований, закриваємо модалку і перенаправляємо
            if (err.message.includes('токен прострочено') || err.message.includes('токен невалідний') || err.message.includes('токен авторизації не знайдено')) {
                setIsProductModalOpen(false);
                alert('Ваша сесія закінчилась. Будь ласка, увійдіть знову.');
                // Перенаправляємо на сторінку логіну
                window.location.href = '/room-for-big-guys-qmttydsra-53231998/login';
            } else {
                alert(`Помилка створення товару: ${err.message}`);
            }
        } finally {
            setIsModalSaving(false); // Скидаємо індикатор
        }
    };

    // Визначення колонок зі зміненими розмірами
    const columns = useMemo(() => [
        {
            accessorKey: 'id',
            header: 'ID',
            size: 120,
            enableEditing: false,
            cell: info => info.getValue(), 
        },
        {
            accessorKey: 'name_ua',
            header: 'Назва',
            size: 200,
            cell: info => <div title={info.getValue()}>{info.getValue()}</div>, 
        },
        {
            id: 'brandModel',
            header: 'Марка / Модель',
            size: 150,
            enableEditing: true, // Дозволяємо редагування
            accessorFn: row => {
                const brandName = row.model?.brand?.name || '';
                const modelName = row.model?.name || '';
                if (brandName && modelName) {
                    return `${brandName} / ${modelName}`;
                } else if (brandName) {
                    return brandName;
                } else if (modelName) {
                    return modelName;
                } else {
                    return '-';
                }
            },
            cell: info => {
                const value = info.getValue();
                return <div title={value}>{value}</div>;
            },
        },
        {
            accessorKey: 'code1',
            header: 'Код 1',
            size: 100,
            cell: info => <div title={info.getValue()}>{info.getValue()}</div>, 
        },
        {
            accessorKey: 'code2',
            header: 'Код 2',
            size: 100,
            cell: info => <div title={info.getValue()}>{info.getValue()}</div>, 
        },
        {
            accessorKey: 'code3',
            header: 'Код 3',
            size: 100,
            cell: info => <div title={info.getValue()}>{info.getValue()}</div>, 
        },
        {
            accessorKey: 'numerkatalogowy',
            header: 'Номер каталоговий',
            size: 140,
            cell: info => <div title={info.getValue()}>{info.getValue()}</div>, 
        },
        {
            accessorKey: 'price',
            header: 'Ціна',
            size: 70,
            cell: info => <div title={info.getValue()}>{info.getValue()}</div>, 
        },
        {
            accessorKey: 'originalPrice',
            header: 'Оригінальна ціна',
            size: 120,
            cell: info => <div title={info.getValue()}>{info.getValue()}</div>, 
        },
        {
            accessorKey: 'deliveryPrice',
            header: 'Ціна доставки',
            size: 110,
            cell: info => <div title={info.getValue()}>{info.getValue()}</div>, 
        },
        {
            accessorKey: 'originalId',
            header: 'Original ID',
            size: 90,
            cell: info => <div title={info.getValue()}>{info.getValue()}</div>, 
        },
        {
            accessorKey: 'link',
            header: 'Посилання',
            size: 150,
            cell: info => <div title={info.getValue()}>{info.getValue()}</div>, 
        },
        {
            accessorKey: 'photo',
            header: 'Фото (URL)',
            size: 150,
            cell: info => <div title={info.getValue()}>{info.getValue()}</div>, 
        },
        {
            accessorKey: 'description',
            header: 'Опис',
            size: 250,
            cell: info => <div className="textarea-cell" title={info.getValue()}>{info.getValue()}</div>,
        },
        {
            accessorFn: row => row.category?.id,
            id: 'categoryId',
            header: 'ID Кат.',
            size: 60,
            cell: info => <div title={info.getValue()}>{info.getValue()}</div>, 
        },
        {
            id: 'actions',
            header: 'Дії',
            size: 130,
            cell: ({ row }) => { 
                const originalRow = originalDataMap[row.original.id]; 
                const hasChanged = originalRow && JSON.stringify(originalRow) !== JSON.stringify(row.original);
                const isSaving = savingProductId === row.original.id;
                return (
                    <div className="action-buttons">
                        <button onClick={() => openProductModal(row.original)} className="edit-button" title="Редагувати повністю" disabled={isSaving}>✎</button>
                        <button onClick={() => handleSave(row.original)} className="save-button" disabled={!hasChanged || isSaving}>
                            {isSaving ? '...' : 'Збер.'}
                        </button>
                        <button onClick={() => handleDelete(row.original.id)} className="delete-button icon-button" title="Видалити" disabled={isSaving}>×</button>
                    </div>
                );
            },
        },
    ], [originalDataMap, openEditModal, savingProductId]);

    // Створення таблиці
    const table = useReactTable({
        data,
        columns,
        pageCount: pageCount,
        state: {
            pagination,
        },
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,
        meta: {
            updateData: (rowIndex, columnId, value) => {
                // Зберігаємо позицію скролу перед оновленням стану
                scrollPositionRef.current = window.scrollY;
                shouldPreserveScrollRef.current = true;

                setData(old =>
                    old.map((row, index) => {
                        if (index === rowIndex) {
                            const updatedRow = { ...row };
                            if (columnId === 'categoryId') {
                                updatedRow.category = { ...row.category, id: value === '' ? null : Number(value) };
                            } else {
                                updatedRow[columnId] = value;
                            }
                            return updatedRow;
                        }
                        return row;
                    })
                );
            },
        },
        debugTable: false,
    });

    // Функція збереження змін після РЕДАГУВАННЯ (реалізація)
    const handleSave = async (rowData) => {
        // НЕ встановлюємо loading:true щоб не приховувати таблицю
        setSavingProductId(rowData.id); // Відмічаємо що зберігаємо цей товар
        setError(null);
        console.log('Відправка даних для оновлення товару:', rowData);

        try {
            // Перевіряємо наявність токену
            const authToken = localStorage.getItem('authToken');
            if (!authToken) {
                throw new Error('Токен авторизації не знайдено. Будь ласка, увійдіть знову.');
            }
            
            // Перевіряємо валідність токена перед оновленням
            try {
                const validateResponse = await fetch(`${BACKEND_URL}/api/auth/validate`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${authToken}`,
                    }
                });
                
                if (!validateResponse.ok) {
                    localStorage.removeItem('authToken');
                    throw new Error('Ваш токен прострочено. Будь ласка, увійдіть знову.');
                }
                console.log('[ProductsAdmin] Token validation successful for update product');
            } catch (validateError) {
                console.error('[ProductsAdmin] Token validation failed:', validateError);
                localStorage.removeItem('authToken');
                throw new Error('Ваш токен прострочено. Будь ласка, увійдіть знову.');
            }
            
            // Переконуємось, що categoryId є числом перед відправкою
            // (хоча rowData вже має містити об'єкт category)
            const dataToSend = {
                 ...rowData,
            };
            
            // Перевірка, чи category є об'єктом
            if (rowData.category && typeof rowData.category.id === 'number') {
                 // Все гаразд, структура вже правильна
            } else if (rowData.categoryId && !rowData.category) {
                 // Якщо є тільки categoryId, перетворюємо на об'єкт
                 dataToSend.category = { id: Number(rowData.categoryId) };
                 delete dataToSend.categoryId;
            }
            
            // Переконуємось, що model містить тільки id для бекенду
            if (rowData.model && rowData.model.id) {
                dataToSend.model = { id: rowData.model.id };
            } else if (rowData.model === null) {
                dataToSend.model = null;
            }

            const response = await fetch(`${BACKEND_URL}/products/${rowData.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`,
                },
                body: JSON.stringify(dataToSend), // Передаємо оновлені дані
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP помилка! Статус: ${response.status}, Текст: ${errorText || 'N/A'}`);
            }

            const updatedProduct = await response.json();
            console.log('Товар успішно оновлено:', updatedProduct);

            // Зберігаємо позицію скролу перед оновленням стану
            scrollPositionRef.current = window.scrollY;
            shouldPreserveScrollRef.current = true;

            // Оновлюємо originalDataMap ДАНИМИ З СЕРВЕРА
            setOriginalDataMap(prevMap => ({ ...prevMap, [updatedProduct.id]: updatedProduct }));
            // Оновлюємо дані в таблиці, щоб відобразити серверні дані
            setData(prevData => prevData.map(item => item.id === updatedProduct.id ? updatedProduct : item));
            // Прибираємо alert
            // alert('Зміни успішно збережено!');

        } catch (err) {
            console.error('Помилка оновлення товару:', err);
            setError(`Помилка оновлення товару ID ${rowData.id}: ${err.message}`);
            
            // Якщо токен протермінований, перенаправляємо
            if (err.message.includes('токен прострочено') || err.message.includes('токен невалідний') || err.message.includes('токен авторизації не знайдено')) {
                alert('Ваша сесія закінчилась. Будь ласка, увійдіть знову.');
                window.location.href = '/room-for-big-guys-qmttydsra-53231998/login';
            } else {
                alert(`Помилка збереження змін: ${err.message}`);
            }
        } finally {
            setSavingProductId(null); // Скидаємо індикатор збереження
        }
    };

    // Функція ВИДАЛЕННЯ товару (реалізація)
    const handleDelete = async (productId) => {
        // Прибираємо window.confirm
        // НЕ встановлюємо loading:true щоб не приховувати таблицю
        setError(null);
        console.log('Запит на видалення товару ID:', productId);

        try {
            // Перевіряємо наявність токену
            const authToken = localStorage.getItem('authToken');
            if (!authToken) {
                throw new Error('Токен авторизації не знайдено. Будь ласка, увійдіть знову.');
            }
            
            // Перевіряємо валідність токена перед видаленням
            try {
                const validateResponse = await fetch(`${BACKEND_URL}/api/auth/validate`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${authToken}`,
                    }
                });
                
                if (!validateResponse.ok) {
                    localStorage.removeItem('authToken');
                    throw new Error('Ваш токен прострочено. Будь ласка, увійдіть знову.');
                }
                console.log('[ProductsAdmin] Token validation successful for delete product');
            } catch (validateError) {
                console.error('[ProductsAdmin] Token validation failed:', validateError);
                localStorage.removeItem('authToken');
                throw new Error('Ваш токен прострочено. Будь ласка, увійдіть знову.');
            }
            
            const response = await fetch(`${BACKEND_URL}/products/${productId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP помилка! Статус: ${response.status}, Текст: ${errorText || 'N/A'}`);
            }

            console.log('Товар успішно видалено ID:', productId);

            // Зберігаємо позицію скролу перед оновленням стану
            scrollPositionRef.current = window.scrollY;
            shouldPreserveScrollRef.current = true;

            // Оновлюємо локальний стан без перезавантаження сторінки
            setData(prevData => prevData.filter(item => item.id !== productId));
            setOriginalDataMap(prevMap => {
                const newMap = { ...prevMap };
                delete newMap[productId];
                return newMap;
            });
            // Прибираємо alert
            // alert('Товар успішно видалено!');

        } catch (err) {
            console.error('Помилка видалення товару:', err);
            setError(`Помилка видалення товару ID ${productId}: ${err.message}`);
            
            // Якщо токен протермінований, перенаправляємо
            if (err.message.includes('токен прострочено') || err.message.includes('токен невалідний') || err.message.includes('токен авторизації не знайдено')) {
                alert('Ваша сесія закінчилась. Будь ласка, увійдіть знову.');
                window.location.href = '/room-for-big-guys-qmttydsra-53231998/login';
            } else {
                alert(`Помилка видалення товару: ${err.message}`);
            }
        }
        // НЕ встановлюємо setLoading(false) - бо не встановлювали true
    };

    const handleDeleteAll = async () => {
        if (btoa(deleteAllPassword) !== CONFIRM_HASH) {
            setDeleteAllError('Невірний пароль');
            return;
        }

        setDeleteAllLoading(true);
        setDeleteAllError('');

        try {
            const authToken = localStorage.getItem('authToken');
            if (!authToken) throw new Error('Токен авторизації не знайдено.');

            const response = await fetch(`${BACKEND_URL}/products/admin/all`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${authToken}` },
            });

            if (!response.ok) {
                const text = await response.text();
                throw new Error(`HTTP ${response.status}: ${text || 'N/A'}`);
            }

            setData([]);
            setOriginalDataMap({});
            setShowDeleteAllModal(false);
            setDeleteAllPassword('');
        } catch (err) {
            setDeleteAllError(`Помилка: ${err.message}`);
        } finally {
            setDeleteAllLoading(false);
        }
    };

    if (loading && !selectedCategoryId) return <div>Завантаження категорій...</div>; // Якщо завантажуються категорії
    if (error && !selectedCategoryId) return <div className="error-message">Помилка завантаження категорій: {error}</div>; // Якщо помилка завантаження категорій

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h2 style={{ margin: 0 }}>Керування Товарами</h2>
                <button
                    className="delete-all-button"
                    onClick={() => { setShowDeleteAllModal(true); setDeleteAllPassword(''); setDeleteAllError(''); }}
                    type="button"
                >
                    🗑 Видалити всі товари
                </button>
            </div>

            {/* Модальне вікно підтвердження видалення всіх */}
            {showDeleteAllModal && (
                <div className="modal-overlay" onClick={() => setShowDeleteAllModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
                        <h3 style={{ marginTop: 0, color: '#c0392b' }}>⚠️ Видалити всі товари</h3>
                        <p style={{ color: '#555' }}>
                            Ця дія незворотна. Введіть пароль підтвердження щоб продовжити.
                        </p>
                        <input
                            type="password"
                            value={deleteAllPassword}
                            onChange={e => { setDeleteAllPassword(e.target.value); setDeleteAllError(''); }}
                            placeholder="Пароль підтвердження"
                            style={{ width: '100%', padding: '8px', marginBottom: '8px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px' }}
                            onKeyDown={e => e.key === 'Enter' && handleDeleteAll()}
                            autoFocus
                        />
                        {deleteAllError && (
                            <p style={{ color: '#c0392b', margin: '0 0 8px', fontSize: '0.9rem' }}>{deleteAllError}</p>
                        )}
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setShowDeleteAllModal(false)}
                                className="cancel-button"
                                type="button"
                                disabled={deleteAllLoading}
                            >
                                Скасувати
                            </button>
                            <button
                                onClick={handleDeleteAll}
                                className="confirm-delete-button"
                                type="button"
                                disabled={deleteAllLoading || !deleteAllPassword}
                            >
                                {deleteAllLoading ? 'Видалення...' : 'Підтвердити'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Фільтр категорій */}
            <div className="admin-filter-container">
                <label htmlFor="category-select">Категорія: </label>
                <select
                    id="category-select"
                    value={selectedCategoryId}
                    onChange={handleCategoryChange}
                    disabled={allCategories.length === 0 || loading} // Блокуємо під час завантаження
                >
                    <option value="">-- Виберіть категорію --</option>
                    {allCategories.map(cat => (
                        <option key={cat.id} value={cat.id}>
                            {cat.name}
                        </option>
                    ))}
                </select>
            </div>

            {/* Кнопки створення та завантаження (з'являються ПІСЛЯ вибору категорії) */} 
            {selectedCategoryId && (
                 <div style={{ marginBottom: '15px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <button onClick={openCreateProductModal} className="create-button">
                        + Створити новий товар у цій категорії
                    </button>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <label htmlFor="file-upload" className="upload-button">
                            📁 Завантажити з файлу
                        </label>
                        <input
                            id="file-upload"
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            onChange={handleFileUpload}
                            style={{ display: 'none' }}
                            disabled={uploadingFile}
                        />
                        {uploadingFile && <span>Завантаження...</span>}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <label htmlFor="compressor-upload" className="upload-button">
                            🔧 Завантажити компресори
                        </label>
                        <input
                            id="compressor-upload"
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            onChange={handleCompressorUpload}
                            style={{ display: 'none' }}
                            disabled={uploadingCompressor}
                        />
                        {uploadingCompressor && <span>Завантаження...</span>}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <label htmlFor="ecu-upload" className="upload-button">
                            💡 Завантажити ЕБУ
                        </label>
                        <input
                            id="ecu-upload"
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            onChange={handleEcuUpload}
                            style={{ display: 'none' }}
                            disabled={uploadingEcu}
                        />
                        {uploadingEcu && <span>Завантаження...</span>}
                    </div>
                </div>
            )}

            {/* Повідомлення про помилку завантаження ТОВАРІВ */} 
            {selectedCategoryId && error && <div className="error-message">Помилка завантаження товарів: {error}</div>}
            
            {/* Повідомлення про помилку завантаження ФАЙЛУ */}
            {selectedCategoryId && uploadError && <div className="error-message">Помилка завантаження файлу: {uploadError}</div>}
            
            {/* Повідомлення про успішне завантаження ФАЙЛУ */}
            {selectedCategoryId && uploadSuccess && (
                <div style={{ color: 'green', backgroundColor: '#d4edda', border: '1px solid #c3e6cb', padding: '10px', borderRadius: '4px', marginBottom: '10px' }}>
                    ✅ Файл успішно завантажено! Товари додано до бази даних.
                </div>
            )}

            {/* Повідомлення компресори */}
            {selectedCategoryId && uploadCompressorError && (
                <div className="error-message">Помилка завантаження компресорів: {uploadCompressorError}</div>
            )}
            {selectedCategoryId && uploadCompressorSuccess && (
                <div style={{ color: 'green', backgroundColor: '#d4edda', border: '1px solid #c3e6cb', padding: '10px', borderRadius: '4px', marginBottom: '10px' }}>
                    ✅ Компресори успішно завантажено!
                </div>
            )}

            {/* Повідомлення ЕБУ */}
            {selectedCategoryId && uploadEcuError && (
                <div className="error-message">Помилка завантаження ЕБУ: {uploadEcuError}</div>
            )}
            {selectedCategoryId && uploadEcuSuccess && (
                <div style={{ color: 'green', backgroundColor: '#d4edda', border: '1px solid #c3e6cb', padding: '10px', borderRadius: '4px', marginBottom: '10px' }}>
                    ✅ ЕБУ успішно завантажено!
                </div>
            )}
            
            {/* Індикатор завантаження ТОВАРІВ */} 
            {selectedCategoryId && loading && <div>Завантаження товарів...</div>}

            {/* Таблиця товарів */}
            {selectedCategoryId && !loading && !error && (
                <>
                    {/* Верхній скролбар */}
                    <div className="admin-table-top-scroll" ref={topScrollRef}>
                        <div className="admin-table-scroll-content"></div>
                    </div>
                    
                    <div className="admin-table-container" ref={bottomScrollRef}>
                        <table className="admin-table">
                             <thead>
                                {table.getHeaderGroups().map(headerGroup => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map(header => (
                                            <th key={header.id} style={{ width: header.getSize() ? `${header.getSize()}px` : undefined }}>
                                                {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody>
                                {table.getRowModel().rows.length === 0 ? (
                                     <tr>
                                         <td colSpan={columns.length} style={{ textAlign: 'center' }}>
                                             Немає товарів для цієї категорії.
                                         </td>
                                     </tr>
                                ) : (
                                    table.getRowModel().rows.map(row => (
                                        <tr key={row.id}>
                                            {row.getVisibleCells().map(cell => {
                                                // Визначаємо, чи є комірка редагованою (не ID і не Дії)
                                                const isEditable = cell.column.id !== 'id' && cell.column.id !== 'actions';
                                                return (
                                                    <td 
                                                        key={cell.id} 
                                                        style={{ width: cell.column.getSize() ? `${cell.column.getSize()}px` : undefined }}
                                                        // Додаємо onClick ТІЛЬКИ до редагованих комірок
                                                        onClick={isEditable ? () => openEditModal(cell.getContext()) : undefined}
                                                        // Додаємо клас ТІЛЬКИ до редагованих комірок
                                                        className={isEditable ? 'editable-td' : ''}
                                                    >
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Пагінація ТІЛЬКИ якщо є сторінки */} 
                    {pageCount > 0 && (
                         <div className="pagination-controls">
                            <button onClick={() => setPageIndex(0)} disabled={!table.getCanPreviousPage()}>{'<<'}</button>
                             <button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>{'<'}</button>
                             <span>
                                 Сторінка{' '}
                                 <strong>
                                     {table.getState().pagination.pageIndex + 1} з {table.getPageCount()}
                                 </strong>{' '}
                             </span>
                            <input
                                type="number"
                                value={table.getState().pagination.pageIndex + 1}
                                onChange={e => {
                                    const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                    if (page >= 0 && page < table.getPageCount()) {
                                        setPageIndex(page);
                                    }
                                }}
                                style={{ width: '60px', margin: '0 5px' }}
                                min={1}
                                max={table.getPageCount()}
                            />
                             <button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>{'>'}</button>
                             <button onClick={() => setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()}>{'>>'}</button>
            
                             <select
                                 value={table.getState().pagination.pageSize}
                                 onChange={e => {
                                     setPageIndex(0);
                                     table.setPageSize(Number(e.target.value));
                                 }}
                                 style={{ marginLeft: '10px' }}
                             >
                                 {[10, 20, 30, 40, 50, 100].map(pageSize => (
                                     <option key={pageSize} value={pageSize}>
                                         Показати {pageSize}
                                     </option>
                                 ))}
                             </select>
                         </div>
                    )}
                 </>
            )}
            
            {/* Повідомлення, якщо категорія не вибрана */} 
            {!selectedCategoryId && !error && (
                <p style={{ marginTop: '20px', textAlign: 'center' }}>Будь ласка, виберіть категорію для відображення та створення товарів.</p>
            )}

            {/* Модальне вікно редагування ОКРЕМОГО ПОЛЯ */} 
            <EditModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                initialValue={editModalData.value}
                onSave={handleSingleFieldModalSave} // Використовуємо нову функцію збереження
                fieldType={editModalData.fieldType}
                fieldLabel={editModalData.fieldLabel}
                brands={brands} // Передаємо список марок
                models={models} // Передаємо список моделей
            />

             {/* Модальне вікно СТВОРЕННЯ/РЕДАГУВАННЯ ТОВАРУ */} 
            <ProductModal
                isOpen={isProductModalOpen}
                onClose={() => setIsProductModalOpen(false)}
                onSave={handleSaveProduct} // Універсальна функція для створення і редагування
                initialData={productModalInitialData} // Передаємо початкові дані
                categories={allCategories} // Передаємо список категорій для select
                brands={brands} // Передаємо список марок
                models={models} // Передаємо список моделей
                isSaving={isModalSaving} // Передаємо індикатор збереження
            />
        </div>
    );
};

export default ProductsAdmin; 